import { ref } from 'vue'

/**
 * Purpose: common account time formatting/default days for Web and print assembly.
 * Invariants: IANA from authenticated API; explicit zone, ISO instants, civil dates preserved; no browser-local fallback.
 * Authorization: display only, never grants permission. Transaction: no writes; settings are saved by the dedicated API.
 * Idempotency: same instant/zone formats identically. Failure: invalid server zone throws, invalid/absent display values stay blank.
 * Verification: tenant-time.test.ts and tenant-timezone.spec.ts under an LA browser with genuine cloud rendering.
 */
export const tenantTimeZoneId = ref('Asia/Shanghai')
export function setTenantTimeZone(id: string): void {
  new Intl.DateTimeFormat('en', { timeZone: id }).format(0) // fail if the contract contains an unsupported zone
  tenantTimeZoneId.value = id
}
/** ISO instants use tenant IANA rules. Date-only business values keep their original civil date; absent/invalid values are blank. */
export function tenantDateTime(value: string | Date | null | undefined, audit = false): string {
  if (!value) return ''
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/u.test(value)) return value
  // Offset-free datetimes are not instants and must not be interpreted in browser time.
  if (typeof value === 'string' && !/(Z|[+-]\d{2}:\d{2})$/iu.test(value)) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (!Number.isFinite(date.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tenantTimeZoneId.value, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', ...(audit ? { second: '2-digit' } : {}), hourCycle: 'h23' }).formatToParts(date)
  const p = (key: Intl.DateTimeFormatPartTypes) => parts.find(v => v.type === key)?.value ?? ''
  return `${p('year')}-${p('month')}-${p('day')} ${p('hour')}:${p('minute')}${audit ? ':' + p('second') : ''}`
}
export const tenantDate = (value: string | Date | null | undefined): string => tenantDateTime(value).slice(0, 10)
export const tenantToday = (): string => tenantDate(new Date())
/** Calendar arithmetic is performed on civil dates, never browser local dates. */
export function addCivilDays(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}
/** Resolve tenant midnight to a UTC boundary; recalculate offset for each date (including DST). */
export function tenantDayStart(day: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(day)) return undefined
  let at = Date.parse(`${day}T00:00:00Z`)
  for (let i = 0; i < 4; i++) {
    const local = tenantDateTime(new Date(at), true)
    const delta = Date.parse(`${day}T00:00:00Z`) - Date.parse(local.replace(' ', 'T') + 'Z')
    if (delta === 0) return new Date(at).toISOString()
    at += delta
  }
  return new Date(at).toISOString()
}
